import React, { Fragment, useContext, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Code2, Maximize2, Minimize2 } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import HighlightedTextarea from "./HighlightedTextarea";

/**
 * Shared Add/Edit modal for a single Assignment Rule -- same chrome as
 * CompletionRuleModal.js (Dialog/Transition, rounded panel with the scroll
 * living on an inner wrapper so the scrollbar never squares off the rounded
 * corner, sticky header/footer, no Delete button here -- delete stays on the
 * table row's trash icon, same as Completion Rules).
 *
 * Purely controlled/presentational -- AssignmentRules.js still owns every
 * field's state and every handler (renderAssignPicker, handleToggle,
 * handleSubmit/handleUpdate). "Select Activity and Map" is deliberately NOT
 * here -- it opens its own Dialog (ActivityMapFilter), and nesting a second
 * Dialog inside this one stacked two modals on screen at once, so that
 * trigger lives as its own icon on the table row instead (AssignmentRules.js).
 * This component only arranges what it's given; no fetching/service calls
 * of its own.
 */
const AssignmentRuleModal = ({
  open,
  onClose,
  isNew,
  ruleNumber,
  ruleNameInputProps,
  assignToNode,
  isActive,
  onToggleActive,
  criteria,
  onCriteriaChange,
  onSave,
  saveDisabled,
}) => {
  const { langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";
  const [criteriaFullscreen, setCriteriaFullscreen] = useState(false);

  const criteriaEditor = (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
          <Code2 size={14} className="text-[#236DB4]" /> {translations[langMode].criteria} &middot; {translations[langMode].expressionEditor}
        </div>
        <button
          type="button"
          onClick={() => setCriteriaFullscreen((f) => !f)}
          className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
        >
          {criteriaFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          {criteriaFullscreen ? translations[langMode].exitFullscreen : translations[langMode].fullscreen}
        </button>
      </div>
      <div className={criteriaFullscreen ? "p-3" : "p-3 max-h-72 overflow-y-auto"}>
        <HighlightedTextarea
          value={criteria}
          onChange={onCriteriaChange}
          rows={criteriaFullscreen ? 16 : 5}
          placeholder={translations[langMode].assignmentCriteriaPlaceholder}
          showLineNumbers
        />
      </div>
    </div>
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>
        <div className="fixed inset-0 z-0 flex items-center justify-center p-2 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden"
              style={{ maxHeight: "90vh" }}
            >
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                  <Dialog.Title className="text-base font-semibold text-gray-800 truncate">
                    {isNew
                      ? translations[langMode].NewCriteria
                      : `${translations[langMode].editCriteria}${Number.isInteger(ruleNumber) ? ` #${ruleNumber}` : ""}`}
                  </Dialog.Title>
                  <ModalCloseButton onClick={onClose} />
                </div>

                <div className="px-4 sm:px-6 py-5 space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {translations[langMode].rulename}
                    </label>
                    <input
                      {...ruleNameInputProps}
                      className="w-full h-11 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block px-3"
                    />
                  </div>

                  <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                    {assignToNode}
                  </div>

                  <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">
                            {translations[langMode].activedeactive}
                          </span>
                          <span
                            className={`text-xs font-semibold px-1.5 py-0.5 rounded ${isActive ? "" : "bg-gray-100 text-gray-500"}`}
                            style={isActive ? { color: "#188A4B", background: "#E7F6ED" } : undefined}
                          >
                            {isActive ? "ON" : "OFF"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5 leading-snug">
                          {translations[langMode]?.assignmentRuleActiveDescription || "When enabled, this rule is evaluated for matching tasks."}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                        onClick={onToggleActive}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#236DB4] ${isActive ? "" : "bg-gray-200"}`}
                        style={isActive ? { background: "#188A4B" } : undefined}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            isActive ? (isRtl ? "-translate-x-6" : "translate-x-6") : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {translations[langMode].criteria}
                    </label>
                    {!criteriaFullscreen && criteriaEditor}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
                  <CommonButton colorIntent="danger"
                    onClick={onClose}
                    variant="danger"
                  >
                    {translations[langMode].cancle}
                  </CommonButton>
                  <CommonButton colorIntent="success"
                    onClick={onSave}
                    variant="primary"
                    disabled={saveDisabled}
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isNew ? translations[langMode].submit : translations[langMode].update}
                  </CommonButton>
                </div>
              </div>

              {criteriaFullscreen && (
                <div
                  className="fixed inset-0 z-[60] bg-gray-900/60 flex items-center justify-center p-8"
                  onClick={() => setCriteriaFullscreen(false)}
                >
                  <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                    {criteriaEditor}
                  </div>
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AssignmentRuleModal;
